import {
  Flex,
  Progress,
  Card,
  useColorModeValue,
  Skeleton,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { GetLimitedPartnerMappingsForAliasingQueryGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { MatchStatus } from './types';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';

export const calculateMatchCounts = (
  aliasingData: GetLimitedPartnerMappingsForAliasingQueryGQLType,
) => {
  let matchedMappings = 0;
  let notMatchedMappings = 0;
  aliasingData.limitedPartnerMappingsByFacility.forEach(
    (mappingsForFacility) => {
      mappingsForFacility.mappings.forEach((mapping) => {
        matchedMappings += mapping.limitedPartnerId ? 1 : 0;
        notMatchedMappings += mapping.limitedPartnerId ? 0 : 1;
      });
    },
  );
  return { matched: matchedMappings, notMatched: notMatchedMappings };
};

export const useMatchCounts = (
  aliasingData: GetLimitedPartnerMappingsForAliasingQueryGQLType,
) => {
  const { matched, notMatched } = useMemo(() => {
    return calculateMatchCounts(aliasingData);
  }, [aliasingData]);
  return { matched, notMatched };
};

type StatusCardProps = {
  type: MatchStatus;
  mappingsTotal: number; // total number of mappings (matches and mismatches)
  mappingsCount: number; // number of mappings of the current type
  matchStatus: MatchStatus;
  updateMatchStatus: (statusCardType: MatchStatus) => void;
};
const StatusCard = ({
  type,
  mappingsTotal,
  mappingsCount,
  matchStatus,
  updateMatchStatus,
}: StatusCardProps) => {
  const borderColor = useColorModeValue('gray.500', 'gray.200');
  const bgHover = useColorModeValue('gray.50', 'gray.800');

  return (
    // !TODO @eldarshamukhamedov @design-system Replace with clickable `<Card />` variant
    <Card
      variant="outline"
      padding="16px"
      width="100%"
      _hover={{
        cursor: 'pointer',
        background: bgHover,
      }}
      borderColor={matchStatus === type ? borderColor : 'auto'}
      borderWidth={matchStatus === type ? '2px' : '1px'}
      onClick={() => updateMatchStatus(type)}
    >
      <Flex alignItems="center" gap="16px">
        <Icon
          icon={type === 'matched' ? 'circle-check' : 'circle-xmark'}
          color={type === 'matched' ? colors.green[400] : colors.orange[400]}
          size="large"
        />
        <Flex direction="column">
          <Typography variant="detail-muted">
            {commonUtils.toTitleCase(String(type))}
          </Typography>
          <Typography TextProps={{ mt: '8px' }} fontWeight="semibold">
            {mappingsCount}
          </Typography>
        </Flex>
      </Flex>
      <Progress
        mt="16px"
        value={
          mappingsCount === mappingsTotal // handle 0/0 case
            ? 100
            : (mappingsCount / mappingsTotal) * 100
        }
        colorScheme={type === 'matched' ? 'green' : 'orange'}
        size="sm"
        borderRadius="4px"
      />
    </Card>
  );
};

const MatchStatusCards = ({
  aliasingData,
  matchStatusForTables,
  setMatchStatusForTables,
}: {
  aliasingData: GetLimitedPartnerMappingsForAliasingQueryGQLType;
  matchStatusForTables: MatchStatus;
  setMatchStatusForTables: React.Dispatch<React.SetStateAction<MatchStatus>>;
}) => {
  const { matched, notMatched } = useMatchCounts(aliasingData);

  const updateMatchStatus = (statusCardType: MatchStatus) => {
    setMatchStatusForTables((prevStatus) => {
      if (prevStatus === statusCardType) {
        return 'all';
      }
      return statusCardType;
    });
  };

  return (
    <>
      <StatusCard
        type="notMatched"
        mappingsTotal={matched + notMatched}
        mappingsCount={notMatched}
        matchStatus={matchStatusForTables}
        updateMatchStatus={updateMatchStatus}
      />
      <StatusCard
        type="matched"
        mappingsTotal={matched + notMatched}
        mappingsCount={matched}
        matchStatus={matchStatusForTables}
        updateMatchStatus={updateMatchStatus}
      />
    </>
  );
};

export const MatchStatusCardsContainer = ({
  aliasingData,
  matchStatusForTables,
  setMatchStatusForTables,
}: {
  aliasingData: GetLimitedPartnerMappingsForAliasingQueryGQLType | undefined;
  matchStatusForTables: MatchStatus;
  setMatchStatusForTables: React.Dispatch<React.SetStateAction<MatchStatus>>;
}) => {
  return (
    <Flex mt="2.5rem" gap="1.5rem" width="100%">
      {aliasingData ? (
        <MatchStatusCards
          aliasingData={aliasingData}
          matchStatusForTables={matchStatusForTables}
          setMatchStatusForTables={setMatchStatusForTables}
        />
      ) : (
        <>
          <Skeleton height="7rem" width="100%" />
          <Skeleton height="7rem" width="100%" />
        </>
      )}
    </Flex>
  );
};
